// Phase 19 — Lifted Supplies overview card (role-aware).
//
// Drops into the affiliate / lifted_partner dashboard as a self-contained
// widget that surfaces:
//   • KPIs (active cities, stores, same-day products, drivers, live orders)
//   • Cities preview list
//   • Stores list with same-day filter + city filter
//   • Map snippet (server-side affiliate-safe slice)
//
// All data via /api/lifted-supplies/* — server-side enforces role gate + PII
// scrub, so this component never has to filter sensitive fields client-side.

import React, { useEffect, useState, useCallback } from "react";
import {
  Globe2, Store, Truck, Sparkles, MapPin, Loader2, ChevronRight,
} from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { Link } from "react-router-dom";

function Kpi({ label, value, icon: Icon, testid, tone = "lifted-green" }) {
  return (
    <div className="glass-panel p-3" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[0.65rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase">
        <Icon className={`h-3 w-3 text-${tone}`} /> {label}
      </div>
      <div className="font-anton text-2xl text-white mt-0.5">{value ?? 0}</div>
    </div>
  );
}

export default function LiftedSuppliesOverview() {
  const [data, setData] = useState(null);
  const [stores, setStores] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(true);
  const [sameDayOnly, setSameDayOnly] = useState(false);

  const load = useCallback(async () => {
    setBusy(true); setErr("");
    try {
      const [{ data: o }, { data: s }] = await Promise.all([
        api.get("/lifted-supplies/me/overview"),
        api.get("/lifted-supplies/me/stores", {
          params: { limit: 12, same_day: sameDayOnly ? 1 : 0 },
        }),
      ]);
      setData(o);
      setStores(s.stores || []);
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setBusy(false); }
  }, [sameDayOnly]);

  useEffect(() => { load(); }, [load]);

  if (err) {
    return (
      <div className="glass-panel p-4 text-rose-300 text-sm" data-testid="lso-error">
        {err}
      </div>
    );
  }
  if (busy && !data) {
    return (
      <div className="glass-panel p-4 text-lifted-dim text-sm flex items-center gap-2" data-testid="lso-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading Lifted Supplies overview…
      </div>
    );
  }
  if (!data) return null;

  return (
    <section className="space-y-4" data-testid="lifted-supplies-overview">
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="text-lifted-green font-bebas tracking-[0.22em] text-[0.65rem] flex items-center gap-1.5">
            <Sparkles className="h-3 w-3" /> LIFTED SUPPLIES NETWORK
          </div>
          <h2 className="font-anton text-2xl uppercase text-white mt-0.5">
            Cities, stores, same-day opportunities
          </h2>
        </div>
        <Link to="/lifted-supplies" className="text-xs text-lifted-green hover:underline inline-flex items-center"
              data-testid="lso-full-link">
          Full view <ChevronRight className="h-3 w-3" />
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="lso-kpis">
        <Kpi label="Active cities"        value={data.kpis.active_cities}         icon={Globe2}   testid="lso-kpi-cities" />
        <Kpi label="Partner stores"       value={data.kpis.active_partner_stores} icon={Store}    testid="lso-kpi-stores" />
        <Kpi label="Same-day products"    value={data.kpis.same_day_products}     icon={Sparkles} testid="lso-kpi-sameday" />
        <Kpi label="Active drivers"       value={data.kpis.active_drivers}        icon={Truck}    testid="lso-kpi-drivers" />
        <Kpi label="Live orders"          value={data.kpis.live_orders_in_flight} icon={MapPin}   testid="lso-kpi-live" />
      </div>

      {/* Cities preview */}
      {(data.cities_preview || []).length > 0 ? (
        <div className="glass-panel p-3" data-testid="lso-cities-preview">
          <div className="text-[0.65rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase mb-1.5">
            Active cities
          </div>
          <div className="flex flex-wrap gap-1.5">
            {data.cities_preview.map((c) => (
              <span key={c} className="px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-xs">
                {c}
              </span>
            ))}
          </div>
        </div>
      ) : (
        <div className="glass-panel p-4 text-sm text-lifted-dim" data-testid="lso-empty-cities">
          No active Lifted Supplies cities yet. Once partner stores activate, they&apos;ll show up here so you can
          promote same-day opportunities.
        </div>
      )}

      {/* Stores grid */}
      <div className="glass-panel overflow-hidden">
        <div className="px-3 py-2 border-b border-white/5 flex items-center justify-between">
          <div className="font-bebas tracking-[0.22em] text-xs text-white">PARTNER STORES</div>
          <label className="text-[0.6rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase flex items-center gap-1.5">
            <input type="checkbox" checked={sameDayOnly}
                   onChange={(e) => setSameDayOnly(e.target.checked)}
                   className="accent-lifted-green" data-testid="lso-sameday-toggle" />
            Same-day
          </label>
        </div>
        {stores.length === 0 ? (
          <div className="p-4 text-sm text-lifted-dim" data-testid="lso-no-stores">
            No partner stores yet. When stores activate in your area, you&apos;ll be able to generate
            affiliate links for their products straight from here.
          </div>
        ) : (
          <ul className="divide-y divide-white/5 max-h-72 overflow-y-auto">
            {stores.map((s) => (
              <li key={s.id} className="px-3 py-2 flex items-center gap-3" data-testid={`lso-store-${s.id}`}>
                <div className="h-9 w-9 rounded bg-black/40 border border-white/10 grid place-items-center text-lifted-green">
                  <Store className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{s.name || "Unnamed store"}</div>
                  <div className="text-[0.65rem] text-lifted-dim truncate">
                    {[s.city, s.state].filter(Boolean).join(", ") || "Location pending"}
                  </div>
                </div>
                {s.same_day_eligible && (
                  <span className="px-1.5 py-0.5 rounded border border-amber-400/40 bg-amber-400/10 text-amber-300 text-[0.6rem] font-bebas tracking-[0.18em] uppercase inline-flex items-center gap-1">
                    <Truck className="h-2.5 w-2.5" /> Same-day
                  </span>
                )}
                <Link to={`/lifted-supplies/stores/${s.id}`}
                      className="text-xs text-lifted-green hover:underline"
                      data-testid={`lso-store-link-${s.id}`}>
                  View products
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
